import React, { useState, useRef, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";

const MultiSelectDropdown = ({
  label = "Main Locations",
  placeholder = "All Main Locations",
  icon = "solar:city-linear",
  options = [],
  selectedValues = [],
  onChange = () => {},
  loading = false,
  searchable = true,
  className = "",
  style = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Filter options by search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter((opt) => {
      const matchLabel = opt.label?.toLowerCase().includes(term);
      const matchSub = opt.sublabel?.toLowerCase().includes(term);
      return matchLabel || matchSub;
    });
  }, [options, searchTerm]);

  // Toggle individual option
  const handleToggle = (value) => {
    const strVal = String(value);
    const exists = selectedValues.some((v) => String(v) === strVal);
    let next;
    if (exists) {
      next = selectedValues.filter((v) => String(v) !== strVal);
    } else {
      next = [...selectedValues, strVal];
    }
    onChange(next);
  };

  // Select all currently filtered options
  const handleSelectAllFiltered = () => {
    const filteredVals = filteredOptions.map((opt) => String(opt.value));
    const merged = Array.from(new Set([...selectedValues.map(String), ...filteredVals]));
    onChange(merged);
  };

  // Clear all selections
  const handleClearAll = (e) => {
    if (e) e.stopPropagation();
    onChange([]);
  };

  // Compute trigger button text
  const selectedCount = selectedValues.length;
  const triggerLabel = useMemo(() => {
    if (selectedCount === 0) return placeholder;
    if (selectedCount === 1) {
      const found = options.find((o) => String(o.value) === String(selectedValues[0]));
      return found?.label || "1 selected";
    }
    return `${selectedCount} ${label} selected`;
  }, [selectedCount, selectedValues, options, placeholder, label]);

  return (
    <div className={`position-relative d-inline-block ${className}`} ref={dropdownRef} style={style}>
      {/* Trigger Button */}
      <button
        type="button"
        className={`btn btn-sm radius-8 d-inline-flex align-items-center gap-2 text-xs border ${
          selectedCount > 0
            ? "btn-outline-primary bg-primary-50 text-primary-600 border-primary-200 shadow-xs"
            : "btn-outline-neutral-300 text-neutral-700 bg-white"
        }`}
        style={{ height: "34px" }}
        onClick={() => setIsOpen((prev) => !prev)}
        title={selectedCount > 0 ? `${selectedCount} ${label} selected` : placeholder}
      >
        {icon && <Icon icon={icon} className="text-sm flex-shrink-0" />}

        <span
          className="text-truncate fw-medium"
          style={{ maxWidth: "180px" }}
        >
          {triggerLabel}
        </span>

        {selectedCount > 0 && (
          <span className="badge bg-primary-600 text-white rounded-pill px-6 py-2 text-xxs flex-shrink-0">
            {selectedCount}
          </span>
        )}

        {selectedCount > 0 && (
          <span
            role="button"
            className="d-flex align-items-center text-primary-400 hover-text-danger p-0 ms-1 flex-shrink-0"
            title="Clear selection"
            onClick={handleClearAll}
          >
            <Icon icon="solar:close-circle-bold" className="text-sm" />
          </span>
        )}

        <Icon
          icon={isOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
          className="text-xs text-secondary-light ms-auto flex-shrink-0"
        />
      </button>

      {/* Dropdown Menu Popover */}
      {isOpen && (
        <div
          className="dropdown-menu show shadow-lg border radius-12 p-12 position-absolute start-0 mt-2 bg-white"
          style={{
            width: "320px",
            maxHeight: "420px",
            zIndex: 1050,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Header Title */}
          <div className="d-flex align-items-center justify-content-between mb-8 pb-6 border-bottom">
            <span className="text-xs fw-bold text-dark d-flex align-items-center gap-1">
              <Icon icon={icon} className="text-primary-600 text-sm" />
              Filter by {label}
            </span>
            {selectedCount > 0 && (
              <span className="badge bg-primary-100 text-primary-700 radius-4 text-xxs px-6 py-2">
                {selectedCount} selected
              </span>
            )}
          </div>

          {/* Search Box */}
          {searchable && (
            <div className="position-relative mb-8">
              <input
                type="text"
                className="form-control form-control-sm text-xs ps-28 pe-24 radius-6"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute text-secondary-light text-xs"
                style={{ left: "8px", top: "50%", transform: "translateY(-50%)" }}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-link p-0 position-absolute text-secondary-light hover-text-dark"
                  style={{ right: "8px", top: "50%", transform: "translateY(-50%)" }}
                  onClick={() => setSearchTerm("")}
                >
                  <Icon icon="ri-close-line" className="text-xs" />
                </button>
              )}
            </div>
          )}

          {/* Quick Actions (Select All / Clear) */}
          <div className="d-flex align-items-center justify-content-between px-2 mb-8 text-xxs">
            <button
              type="button"
              className="btn btn-link p-0 text-primary-600 fw-medium text-decoration-none text-xxs"
              onClick={handleSelectAllFiltered}
              disabled={filteredOptions.length === 0}
            >
              Select All {filteredOptions.length !== options.length ? `(${filteredOptions.length})` : ""}
            </button>
            <button
              type="button"
              className="btn btn-link p-0 text-danger fw-medium text-decoration-none text-xxs"
              onClick={() => handleClearAll()}
              disabled={selectedCount === 0}
            >
              Clear Selection
            </button>
          </div>

          {/* Options List */}
          <div
            className="overflow-y-auto pe-1"
            style={{ maxHeight: "230px" }}
          >
            {loading ? (
              <div className="text-center py-20 text-secondary-light text-xs">
                <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" />
                Loading {label.toLowerCase()}...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="text-center py-20 text-secondary-light text-xs">
                <Icon icon="solar:info-circle-linear" className="text-lg d-block mx-auto mb-1 text-neutral-400" />
                No {label.toLowerCase()} found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedValues.some((v) => String(v) === String(opt.value));
                return (
                  <label
                    key={String(opt.value)}
                    className={`d-flex align-items-start gap-2 p-8 radius-6 cursor-pointer mb-1 transition-all ${
                      isSelected
                        ? "bg-primary-50 text-primary-900"
                        : "hover-bg-neutral-50 text-neutral-800"
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input mt-1 flex-shrink-0 cursor-pointer"
                      checked={isSelected}
                      onChange={() => handleToggle(opt.value)}
                      style={{ cursor: "pointer" }}
                    />
                    <div className="flex-grow-1 min-w-0">
                      <div className="text-xs fw-semibold text-truncate" title={opt.label}>
                        {opt.label}
                      </div>
                      {opt.sublabel && (
                        <div className="text-xxs text-secondary-light text-truncate mt-1" title={opt.sublabel}>
                          {opt.sublabel}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>

          {/* Footer Done Button */}
          <div className="pt-8 mt-6 border-top d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-xs btn-primary px-12 py-4 radius-6 text-xs fw-semibold"
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;

